import { useState } from 'react';
import { productsApi } from '@/shared/api/endpoints';
import type { Product } from '@/shared/api/types';
import { describeError } from '@/shared/hooks/useAsync';
import { Banner, Button, Field, Sheet } from '@/shared/ui';

/**
 * RF-27 y RF-28 · dar de alta un producto propio.
 *
 * Los valores se cargan por 100 g, que es como los declara el rótulo argentino y como los guarda
 * el catálogo. El producto nace con origen `usuario` y sólo lo ve quien lo cargó: el catálogo
 * compartido no se ensucia con la lectura que alguien hizo de un envase.
 */
export function OwnProductSheet({
  initialName = '',
  initialEan = '',
  onCreated,
  onClose,
}: {
  initialName?: string;
  initialEan?: string;
  onCreated: (product: Product) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(initialName);
  const [brand, setBrand] = useState('');
  const [ean, setEan] = useState(initialEan);
  const [proteinG, setProteinG] = useState('');
  const [carbohydratesG, setCarbohydratesG] = useState('');
  const [fatG, setFatG] = useState('');
  const [fiberG, setFiberG] = useState('');
  const [sodiumMg, setSodiumMg] = useState('');
  const [servingWeightG, setServingWeightG] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const complete = name.trim() !== '' && proteinG !== '' && carbohydratesG !== '' && fatG !== '';

  const submit = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const product = await productsApi.createOwn({
        name: name.trim(),
        brand: brand.trim() === '' ? null : brand.trim(),
        ean: ean.trim() === '' ? null : ean.trim(),
        macros100g: {
          proteinG: Number(proteinG),
          carbohydratesG: Number(carbohydratesG),
          fatG: Number(fatG),
          fiberG: fiberG === '' ? null : Number(fiberG),
          // Los rótulos argentinos no declaran alcohol, así que acá nunca hay qué cargar.
        alcoholG: null,
        sodiumMg: sodiumMg === '' ? null : Number(sodiumMg),
        },
        servingWeightG: servingWeightG === '' ? null : Number(servingWeightG),
      });

      onCreated(product);
      onClose();
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet title="Cargar un producto" onClose={onClose}>
      <p className="small muted">
        Los valores son por 100 g o 100 ml, como figuran en el rótulo. Este producto lo vas a ver
        sólo vos.
      </p>

      {error !== null && <Banner tone="danger">{error}</Banner>}

      <Field
        label="Nombre"
        required
        value={name}
        onChange={(event) => setName(event.target.value)}
        placeholder="Yogur natural"
      />
      <Field
        label="Marca"
        value={brand}
        onChange={(event) => setBrand(event.target.value)}
        hint="Opcional."
      />
      <Field
        label="Código de barras"
        inputMode="numeric"
        value={ean}
        onChange={(event) => setEan(event.target.value)}
        hint="Opcional. Si lo cargás, el próximo escaneo lo encuentra directo."
      />

      <Field
        label="Proteína (g/100 g)"
        type="number"
        inputMode="decimal"
        min={0}
        step="any"
        required
        value={proteinG}
        onChange={(event) => setProteinG(event.target.value)}
      />
      <Field
        label="Carbohidratos (g/100 g)"
        type="number"
        inputMode="decimal"
        min={0}
        step="any"
        required
        value={carbohydratesG}
        onChange={(event) => setCarbohydratesG(event.target.value)}
      />
      <Field
        label="Grasa (g/100 g)"
        type="number"
        inputMode="decimal"
        min={0}
        step="any"
        required
        value={fatG}
        onChange={(event) => setFatG(event.target.value)}
      />
      <Field
        label="Fibra (g/100 g)"
        type="number"
        inputMode="decimal"
        min={0}
        step="any"
        value={fiberG}
        onChange={(event) => setFiberG(event.target.value)}
        hint="Opcional."
      />
      <Field
        label="Sodio (mg/100 g)"
        type="number"
        inputMode="decimal"
        min={0}
        step="any"
        value={sodiumMg}
        onChange={(event) => setSodiumMg(event.target.value)}
        hint="Opcional."
      />
      <Field
        label="Peso de la porción (g)"
        type="number"
        inputMode="decimal"
        min={0}
        step="any"
        value={servingWeightG}
        onChange={(event) => setServingWeightG(event.target.value)}
        hint="Opcional, pero sin esto no vas a poder declarar el consumo en porciones."
      />

      <Button
        variant="primary"
        block
        onClick={() => void submit()}
        disabled={submitting || !complete}
      >
        {submitting ? 'Guardando…' : 'Guardar el producto'}
      </Button>
    </Sheet>
  );
}
